import { Text } from '@chakra-ui/react'

import countries from '@/utils/countries'

import OnboardingInputPronounsRadio from '@/components/Onboarding/OnboardingInputPronounsRadio'
import ShwLink from '@/components/ShwLink'
import type { FormInputType } from '@/components/UI/Form/types'

import { PRONOUN_OPTIONS } from './onboarding/getAboutYouFormInputs'

export default function getEditProfileFormInputs({
  defaultFirstName,
  defaultLastName,
  defaultBio,
  defaultCountry,
  defaultPronouns,
  defaultKhanAcademyUsername,
  defaultAopsUsername,
}: {
  defaultFirstName: string
  defaultLastName: string
  defaultBio: string | null
  defaultCountry: string
  defaultPronouns: string | null

  defaultKhanAcademyUsername: string
  defaultAopsUsername: string
}): FormInputType[] {
  const pronounOptions =
    defaultPronouns == null || PRONOUN_OPTIONS.some(option => option.value === defaultPronouns)
      ? PRONOUN_OPTIONS
      : [...PRONOUN_OPTIONS, { value: defaultPronouns, label: defaultPronouns }]

  return [
    {
      label: 'What is your first name?',
      sublabel: 'Will be displayed publicly.',
      type: 'text',
      name: 'first_name',
      placeholder: 'First Name',
      required: true,
      initialValue: defaultFirstName,
      size: 3,
    },
    {
      label: 'What is your last name?',
      sublabel: 'Only the first letter will be displayed publicly.',
      type: 'text',
      name: 'last_name',
      placeholder: 'Last Name',
      required: true,
      initialValue: defaultLastName,
      size: 3,
    },

    {
      label: 'My Bio',
      sublabel:
        'This will be visible on your profile. Please try to avoid sensitive information like your last name, high school name, etc.',
      type: 'textarea',
      name: 'bio',
      required: true,
      initialValue: defaultBio,
      size: 6,
    },
    {
      label: 'Country',
      type: 'select',
      name: 'country',
      options: countries.map(item => ({
        value: item.code,
        label: item.name,
      })),
      initialValue: defaultCountry,
      sizeSpan: 6,
      size: 3,
    },
    {
      label: 'My Pronouns',
      type: 'radio',
      name: 'pronouns',
      options: pronounOptions,
      required: false,
      initialValue: defaultPronouns,
      Component: OnboardingInputPronounsRadio,
      size: 6,
    },
    {
      label: 'Khan Academy Username',
      type: 'text',
      name: 'khan_academy_username',
      sublabel: (
        <Text color="fg.subtle" variant="supporting">
          If you are a <ShwLink href="http://khanacademy.org">Khan Academy</ShwLink> user, you can
          input your username below. This will be visible on your public profile.{' '}
          <Text as="span" color="fg.subtle" fontWeight="medium" variant="supporting">
            Please do not include the @ that is in front of your Khan Academy username.
          </Text>
        </Text>
      ),
      required: false,
      initialValue: defaultKhanAcademyUsername,
      size: 6,
    },
    {
      label: 'AoPS Username',
      type: 'text',
      name: 'aops_username',
      sublabel: (
        <Text color="fg.subtle" variant="supporting">
          If you are an{' '}
          <ShwLink href="http://artofproblemsolving.com">Art of Problem Solving</ShwLink> user, you
          can input your username below. This will be visible on your public profile.
        </Text>
      ),
      required: false,
      initialValue: defaultAopsUsername,
      size: 6,
    },
  ]
}
